import { useTranslation } from 'next-i18next';
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  VFC,
  useLayoutEffect,
} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import DefaultSpinner from '@/components/DefaultSpinner';
import FilterSideBar from '@/containers/MarketplacePage/FilterSideBar';
import MarketplaceItemsContext from '@/contexts/MarketplaceItemsContext';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import useIsScrollable from '@/hooks/useIsScrollable';
import ChevronRightIcon from '@/icons/chevron-right.svg';

import MarketplaceContents from './MarketplaceContents';
import styles from './MarketplacePage.module.scss';
import MarketplaceProfiles from './MarketplaceProfiles';
import TagScroller from './TagScroller';
import RandomMarketplaceContents from './RandomMarketplaceContents';
import { useRouter } from 'next/router';

const MarketplacePage: VFC = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const [isShowingNFTs, setIsShowingNFTs] = useState(true);
  let vw = 1920;
  if (process.browser) {
    vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
  }
  const {
    creations,
    randomCreations,
    NFTStatus,
    userStatus,
    loadCreations,
    loadRandomCreations,
    filteredUsers,
    loadUsers,
    setSearchConditionNFTs,
    setSearchConditionUsers,
    setIsLoaded,
    searchConditionNFTs,
    searchConditionUsers,
    showRandom,
    setShowRandom,
  } = useContext(MarketplaceItemsContext);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // detect if NFT DOM is scrollable, if not and NFT has more, start loading
  const [isScrollableNFT, NFTRef, NFTNode] = useIsScrollable([creations]);
  useEffect(() => {
    if (NFTNode && !isScrollableNFT && NFTStatus === 'hasMore') {
      loadCreations();
    }
  }, [isScrollableNFT, NFTNode, NFTStatus]);

  // detect if random NFT DOM is scrollable, if not and NFT has more, start loading
  const [isScrollableRandomNFT, randomNFTRef, randomNFTNode] = useIsScrollable([
    creations,
  ]);
  useEffect(() => {
    if (randomNFTNode && !isScrollableRandomNFT && NFTStatus === 'hasMore') {
      loadRandomCreations();
    }
  }, [isScrollableRandomNFT, randomNFTNode, userStatus]);

  // detect if user DOM is scrollable, if not and user has more, start loading
  const [isScrollableUser, userRef, userNode] = useIsScrollable([creations]);
  useEffect(() => {
    if (userNode && !isScrollableUser && userStatus === 'hasMore') {
      loadUsers();
    }
  }, [isScrollableUser, userNode, userStatus]);

  const openSidebar = () => {
    setIsSidebarOpen(true);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    if (vw >= 1020) {
      openSidebar();
    }
  }, []);

  useEffect(() => {
    if (!router.isReady) return;
    if (router.query.tagName && router.query.tagNameJA) {
      setSearchConditionNFTs({
        ...searchConditionNFTs,
        tagNames: [router.query.tagName as string],
        tagNamesJA: [router.query.tagNameJA as string],
      });
    }
  }, [router.isReady]);

  useOnClickOutside(sidebarRef, () => {
    const currentVW = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );

    if (currentVW < 576) {
      closeSidebar();
    }
  });

  useLayoutEffect(() => {
    if (sessionStorage.getItem('isShowingNFTs')) {
      setIsShowingNFTs(sessionStorage.getItem('isShowingNFTs') === 'true');
    } else {
      sessionStorage.setItem('isShowingNFTs', isShowingNFTs.toString());
    }
    if (sessionStorage.getItem('searchConditionUsers')) {
      setSearchConditionUsers(
        JSON.parse(sessionStorage.getItem('searchConditionUsers'))
      );
    }
    if (sessionStorage.getItem('searchConditionNFTs')) {
      setSearchConditionNFTs(
        JSON.parse(sessionStorage.getItem('searchConditionNFTs'))
      );
    }
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    sessionStorage.setItem('isShowingNFTs', isShowingNFTs.toString());
  }, [isShowingNFTs]);
  useEffect(() => {
    sessionStorage.setItem(
      'searchConditionNFTs',
      JSON.stringify(searchConditionNFTs)
    );
  }, [searchConditionNFTs]);
  useEffect(() => {
    sessionStorage.setItem(
      'searchConditionUsers',
      JSON.stringify(searchConditionUsers)
    );
  }, [searchConditionUsers]);
  return (
    <div className={styles.marketplacePageContainer}>
      {isSidebarOpen && (
        <div className={styles.sidebarOverlay}>
          <FilterSideBar
            ref={sidebarRef}
            onCloseButtonClick={closeSidebar}
            isShowingNFTs={isShowingNFTs}
            setIsShowingNFTs={setIsShowingNFTs}
          />
        </div>
      )}
      <div className={styles.marketplaceContentContainer}>
        {/*<h2 id={styles.marketplaceHeading}>Creations Marketplace</h2>*/}
        {!isSidebarOpen && (
          <button
            type="button"
            className={styles.sidebarOpenButton}
            onClick={openSidebar}
          >
            <ChevronRightIcon />
          </button>
        )}
        {isShowingNFTs && showRandom && (
          <div
            id="scrollableDivRandomNFT"
            className={styles.loader}
            ref={randomNFTRef}
          >
            <InfiniteScroll
              className={styles.scroll}
              dataLength={randomCreations.length}
              next={loadRandomCreations}
              hasMore={NFTStatus === 'hasMore' || NFTStatus === 'loadingMore'}
              loader={<DefaultSpinner />}
              endMessage={
                <div className={styles.listBottomMessage}>
                  {t('marketplacePage.loadedAllItems', {
                    count: randomCreations.length,
                  })}
                </div>
              }
              scrollableTarget="scrollableDivRandomNFT"
            >
              <RandomMarketplaceContents />
            </InfiniteScroll>
          </div>
        )}

        {isShowingNFTs && !showRandom && (
          <>
            {NFTStatus === 'hasError' ? (
              <div className={styles.loadingError}>{t('serverError')}</div>
            ) : NFTStatus === 'initializing' ? (
              <DefaultSpinner />
            ) : (
              <div id="scrollableDivNFT" className={styles.loader} ref={NFTRef}>
                <TagScroller isShowingNFTs={isShowingNFTs} />
                <InfiniteScroll
                  className={styles.scroll}
                  dataLength={creations.length}
                  next={loadCreations}
                  hasMore={
                    NFTStatus === 'hasMore' || NFTStatus === 'loadingMore'
                  }
                  loader={<DefaultSpinner />}
                  endMessage={
                    <div className={styles.listBottomMessage}>
                      {t('marketplacePage.loadedAllItems', {
                        count: creations.length,
                      })}
                    </div>
                  }
                  scrollableTarget="scrollableDivNFT"
                >
                  <MarketplaceContents />
                </InfiniteScroll>
              </div>
            )}
          </>
        )}
        {/* TODO: Work on randomize for Users as well */}
        {!isShowingNFTs && (
          <>
            {userStatus === 'hasError' ? (
              <div className={styles.loadingError}>{t('serverError')}</div>
            ) : userStatus === 'initializing' ? (
              <DefaultSpinner />
            ) : (
              <div
                id="scrollableDivRandomUser"
                className={styles.loader}
                ref={userRef}
              >
                <TagScroller isShowingNFTs={isShowingNFTs} />
                <InfiniteScroll
                  className={styles.scroll}
                  dataLength={filteredUsers.length}
                  next={loadUsers}
                  hasMore={
                    userStatus === 'hasMore' || userStatus === 'loadingMore'
                  }
                  loader={<DefaultSpinner />}
                  endMessage={
                    <div className={styles.listBottomMessage}>
                      {t('marketplacePage.loadedAllItems', {
                        count: filteredUsers.length,
                      })}
                    </div>
                  }
                  scrollableTarget="scrollableDivRandomUser"
                >
                  <MarketplaceProfiles />
                </InfiniteScroll>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MarketplacePage;
